/**
 * ChatContainer — Main orchestrator for the chat UI.
 *
 * All state, effects, and handlers live in useChatOrchestrator.
 * This component only composes: ChatMessagesArea + ChatInput + ChatModals.
 */

import React, { useEffect, useCallback, useRef } from "react";
import { useChatOrchestrator } from "../hooks/useChatOrchestrator";
import { ChatInput } from "./ChatInput";
import { ChatMessagesArea } from "./ChatMessagesArea";
import { ChatModals } from "./ChatModals";
import { ChatStatusBar } from "./ChatStatusBar";
import { AsyncProcessingNotice } from "./AsyncProcessingNotice";
import { ConversationStarters } from "./ConversationStarters";
import "../i18n/i18n";
import cogsolAvatar from "../assets/cogsol-logo.svg";

const DEFAULT_AVATAR = cogsolAvatar;

const ChatContainer = (props) => {
  const ctx = useChatOrchestrator(props);
  const { handleFullReset } = ctx;
  const { transcriptionHistory } = ctx.audio;
  const { t } = ctx;
  const { urlLogo, chatName, assistantColors } = ctx.assistant;
  const { setInputValue } = ctx.chat;
  const inputRef = useRef(null);

  // Wire global DOM event to orchestrator reset handler
  useEffect(() => {
    const handler = () => handleFullReset();
    document.addEventListener("resetAssistantProperties", handler);
    return () =>
      document.removeEventListener("resetAssistantProperties", handler);
  }, [handleFullReset]);

  // Conversation starters only apply to the first message of a brand new chat. `question === null`
  // is the canonical "nothing sent yet" signal in useChat, and `messages` excludes the assistant's
  // initial greeting, so an untouched chat really is empty.
  const showConversationStarters =
    ctx.chat.chatId === null &&
    ctx.chat.question === null &&
    ctx.chat.messages.length === 0;

  /** Fill the input with a starter (it is not sent) and hand focus back to the textarea. */
  const handleStarterSelect = useCallback(
    (text) => {
      setInputValue(text);
      // Deferred so the textarea already holds the new value: focusing alone leaves the caret at
      // position 0, so move it to the end for the user to keep typing.
      requestAnimationFrame(() => {
        const el = inputRef.current;
        if (!el) return;
        el.focus();
        el.setSelectionRange(el.value.length, el.value.length);
      });
    },
    [setInputValue],
  );

  /** Render a single audio-mode message. */
  const renderAudioMessage = useCallback((item) => {
    if (item.role !== "user" && item.role !== "assistant") return null;
    const isUser = item.role === "user";
    const messageContent = item.formatted?.transcript || item.formatted?.text;
    const displayContent = transcriptionHistory[item.id]
      ? transcriptionHistory[item.id].transcript
      : messageContent;

    const content = (
      <>
        <div className="text-[15px] leading-relaxed">
          {!displayContent ? (
            <span className="italic text-gray-500">
              {t("generatingTranscription")}...
            </span>
          ) : (
            displayContent
          )}
        </div>
        {item.formatted?.file && (
          <div className="mt-2">
            <audio src={item.formatted.file.url} controls className="w-full" />
          </div>
        )}
      </>
    );

    if (isUser) {
      return (
        <div key={item.id} className="flex justify-end my-6">
          <div className="bg-white rounded-xl max-w-[75%] px-4 py-2.5 text-[15px] shadow-sm">
            {content}
          </div>
        </div>
      );
    }

    return (
      <div key={item.id} className="flex flex-col gap-0.5 my-6">
        <div className="flex items-center gap-1.5 mb-1">
          <img
            src={urlLogo || DEFAULT_AVATAR}
            alt={chatName || t("assistant")}
            className="w-8 h-8 rounded-md object-cover shrink-0"
            onError={(e) => { e.target.src = DEFAULT_AVATAR; }}
          />
          <span
            className="text-xs font-semibold truncate max-w-[200px]"
            style={{ color: assistantColors?.nameColor || "#111827" }}
          >
            {chatName || t("assistant")}
          </span>
        </div>
        {content}
      </div>
    );
  }, [transcriptionHistory, t, urlLogo, chatName, assistantColors]);

  return (
    <div className={`max-w-[900px] w-full mx-auto flex flex-col px-3 sm:px-5 relative bg-[#f1f5f9] sm:rounded-xl sm:border sm:my-3 sm:h-[88dvh] ${ctx.isEmbedded ? "h-dvh" : "h-[calc(100dvh-5rem)]"}`}
      style={{ borderColor: assistantColors?.borderColor || "#e2e8f0" }}>
      <div className="flex-1 flex flex-col min-h-0">
        {ctx.activeRetrievalId && ctx.assistant.asyncAvailable && (
          <ChatStatusBar
            isLoading={ctx.chat.isLoading}
            executionStatus={ctx.chat.executionStatus}
          />
        )}
        <ChatMessagesArea
          loading={ctx.assistant.loading}
          activeRetrievalId={ctx.activeRetrievalId}
          retrievals={ctx.assistant.retrievals}
          onRetrievalChange={ctx.handleRetrievalChange}
          envRetrieval={ctx.envRetrieval}
          isEmbedded={ctx.isEmbedded}
          isConnected={ctx.isConnected}
          items={ctx.items}
          renderAudioMessage={renderAudioMessage}
          messages={ctx.chat.messages}
          iniMessage={ctx.assistant.iniMessage}
          assistantColors={ctx.assistant.assistantColors}
          urlLogo={ctx.assistant.urlLogo}
          chatName={ctx.assistant.chatName}
          executionStatus={ctx.chat.executionStatus}
          waitingOAuthServerId={ctx.chat.waitingOAuthServerId}
          chatId={ctx.chat.chatId}
          onReauthorize={ctx.openWaitingForAuthPopup}
          expandedReferences={ctx.expandedReferences}
          onToggleReferences={ctx.toggleReferences}
          attachments={ctx.attachmentsHook.attachments}
          messageMetadataList={ctx.metadataHook.messageMetadataList}
          allowMetadata={props.allowMetadata}
          onFileMetadataClick={ctx.metadataHook.openFileMetadata}
          activatingMatrix={ctx.activatingMatrix}
        />

        {ctx.activeRetrievalId && (
          <div className="shrink-0 px-0 sm:px-4 pb-2 sm:pb-4 pt-1">
          {ctx.chat.showAsyncNotice && <AsyncProcessingNotice />}
          <ConversationStarters
            starters={ctx.assistant.conversationStarters}
            visible={showConversationStarters}
            disabled={ctx.chat.isLoading || ctx.assistant.allowAsk === false}
            onSelect={handleStarterSelect}
          />
          <ChatInput
            textareaRef={inputRef}
            retrievals={ctx.assistant.retrievals}
            selectedRetrieval={ctx.assistant.selectedRetrieval}
            currentRetrievalDescription={ctx.assistant.currentRetrievalDescription}
            assistantColors={ctx.assistant.assistantColors}
            maxMsgLength={ctx.assistant.maxMsgLength}
            allowAsk={ctx.assistant.allowAsk}
            streamingAvailable={ctx.assistant.streamingAvailable}
            matrixModeAvailable={ctx.assistant.matrixModeAvailable}
            audioAvailable={ctx.assistant.audioAvailable}
            onRetrievalChange={ctx.handleRetrievalChange}
            envRetrieval={ctx.envRetrieval}
            isEmbedded={ctx.isEmbedded}
            hasFixedRetrieval={ctx.hasFixedRetrieval}
            retrievalId={ctx.activeRetrievalId}
            inputValue={ctx.chat.inputValue}
            onInputChange={ctx.chat.setInputValue}
            isLoading={ctx.chat.isLoading}
            onSend={ctx.handleSendMessage}
            chatId={ctx.chat.chatId}
            question={ctx.chat.question}
            onStartNewChat={ctx.handleStartNewChat}
            ended={ctx.chat.ended}
            endedStatus={ctx.chat.endedStatus}
            customErrorMessage={ctx.chat.customErrorMessage}
            executionStatus={ctx.chat.executionStatus}
            activeMatrix={ctx.activeMatrix}
            activatingMatrix={ctx.activatingMatrix}
            realtimeError={ctx.realtimeError}
            getErrorMessage={ctx.getErrorMessage}
            addFeedback={props.addFeedback}
            addFeedback2={props.addFeedback2}
            audioRecorder={ctx.audio.audioRecorder}
            isConnected={ctx.isConnected}
            isTranscribingAudio={ctx.audio.isTranscribingAudio}
            transcribingDots={ctx.audio.transcribingDots}
            realtimeEnabled={ctx.realtimeEnabled}
            realtimeModeEnabled={ctx.audio.realtimeModeEnabled}
            onModeChange={ctx.audio.setRealtimeModeEnabled}
            realtimeConfig={ctx.realtimeConfig}
            allowFiles={props.allow_files}
            allowMetadata={props.allowMetadata}
            messageFiles={ctx.attachmentsHook.messageFiles}
            onFileDelete={ctx.attachmentsHook.removeFile}
            onAttachmentsOpen={ctx.attachmentsHook.openAttachmentsModal}
            onMetadataOpen={ctx.metadataHook.openMetadataModal}
            onMatrixOpen={() => ctx.setMatrixModal(true)}
          />
          </div>
        )}
      </div>

      <ChatModals
        matrixModal={ctx.matrixModal}
        onMatrixConfirm={ctx.executeMatrixMode}
        onMatrixCancel={() => ctx.setMatrixModal(false)}
        statusModal={ctx.statusModal}
        statusMessage={ctx.getModalMessage()}
        onStatusClose={() => {
          ctx.setStatusModal(false);
          ctx.setStreamingError(null);
          if (ctx.audio.audioRecorder.errorCode) ctx.audio.audioRecorder.resetError();
        }}
        allowMetadata={props.allowMetadata}
        metadataHook={ctx.metadataHook}
        assistantColors={ctx.assistant.assistantColors}
        chatMetadataReadOnly={ctx.chat.question !== null || ctx.chat.chatId !== null}
        attachmentsHook={ctx.attachmentsHook}
        attachmentConfig={ctx.assistant.attachmentConfig}
        fileMetadataModal={ctx.metadataHook.fileMetadataModal}
        onCloseFileMetadata={ctx.metadataHook.closeFileMetadata}
      />

    </div>
  );
};

export { ChatContainer };
export default ChatContainer;
